import { useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import {
  Banknote,
  ClipboardList,
  Home,
  ListChecks,
  Loader2,
  LogOut,
  Settings,
  Users,
  Wallet,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useUnitContext } from '@/contexts/UnitContext';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: Array<'treasurer' | 'worshipfulMaster' | 'auditor'>;
}

const NAV_ITEMS: NavItem[] = [
  { to: 'dashboard', label: 'Dashboard', icon: Home },
  { to: 'banking', label: 'Banking', icon: Wallet },
  { to: 'transactions', label: 'Transactions', icon: ListChecks },
  { to: 'members', label: 'Members', icon: Users },
  { to: 'subscriptions', label: 'Subscriptions', icon: Banknote },
  { to: 'reports', label: 'Reports', icon: ClipboardList },
  { to: 'settings', label: 'Settings', icon: Settings, roles: ['treasurer'] },
];

function ShellInner() {
  const { unitId } = useParams<{ unitId: string }>();
  const navigate = useNavigate();
  const { logout, userProfile } = useAuth();
  const {
    activeUnit,
    availableUnitsLoading,
    treasurerSettings,
    treasurerSettingsLoading,
    isTreasurer,
    hasReadAccess,
  } = useUnitContext();

  useEffect(() => {
    if (!unitId) return;
    if (availableUnitsLoading) return;
    if (!activeUnit) {
      navigate('/units', { replace: true });
    }
  }, [unitId, activeUnit, availableUnitsLoading, navigate]);

  if (availableUnitsLoading || (unitId && !activeUnit) || treasurerSettingsLoading || !activeUnit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Module not yet initialised — funnel to setup wizard.
  if (treasurerSettings && !treasurerSettings.isInitialised) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-white rounded-xl shadow-sm border border-emerald-200 p-8">
          <h1 className="text-2xl font-bold mb-2">Set up the Treasurer module</h1>
          <p className="text-gray-600 mb-6">
            Before you can use the Treasurer features for{' '}
            <strong>{activeUnit.name} No. {activeUnit.number}</strong>, a few details
            are required: your financial year, subscription due date, the Grand
            Lodge capitation rate, and the threshold above which large
            transactions trigger an alert.
          </p>
          <button
            onClick={() => navigate(`/u/${activeUnit.unitId}/setup`)}
            className="px-5 py-2.5 bg-emerald-600 text-white rounded-md font-medium hover:bg-emerald-700"
          >
            Begin setup
          </button>
        </div>
      </div>
    );
  }

  if (!hasReadAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <h1 className="text-xl font-bold mb-2">No treasurer access</h1>
          <p className="text-gray-600 mb-4">
            You are a member of this unit but have not been granted Treasurer,
            Worshipful Master, or Auditor access. Ask the current Treasurer to
            grant you a role.
          </p>
          <Link to="/units" className="text-emerald-600 hover:underline">
            Back to units
          </Link>
        </div>
      </div>
    );
  }

  const visibleNavItems = NAV_ITEMS.filter((item) => {
    if (!item.roles) return true;
    if (item.roles.includes('treasurer') && isTreasurer) return true;
    return false;
  });

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wallet className="h-6 w-6 text-emerald-600" />
            <div>
              <div className="text-sm font-semibold text-slate-900">
                {activeUnit.name}{' '}
                <span className="text-slate-500 font-normal">No. {activeUnit.number}</span>
              </div>
              <div className="text-xs text-slate-500">Treasurer</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-slate-600 hidden sm:block">
              {userProfile?.firstName ?? userProfile?.name ?? userProfile?.email}
            </div>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 max-w-7xl mx-auto w-full">
        <aside className="w-56 shrink-0 border-r border-slate-200 bg-white py-4">
          <nav className="flex flex-col">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-emerald-50 text-emerald-700 border-r-2 border-emerald-600'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Shell layout for per-unit treasurer pages. UnitProvider lives in UnitRouteLayout. */
export default function TreasurerShell() {
  return <ShellInner />;
}
